import { isNotEmpty, type DashboardData, type DashboardWidget } from '@terros/common'
import { isPresetWidget } from './widgetType'
import { sortDashboardsByOrder } from './order'
import { NUMBER_WIDGET_FULL_ROW_METRIC_THRESHOLD } from './metricGrid'

/**
 * One dashboard's contribution to the Home feed: the dashboard itself and the
 * widgets of it that can render, in their configured order. Rendered as a
 * single feed section titled with the dashboard's name.
 */
export type DashboardFeedGroup<T extends DashboardData = DashboardData> = {
  dashboard: T
  widgets: DashboardWidget[]
}

/**
 * Widgets in a dashboard that can render in the feed. KPI-backed widgets need
 * at least one configured metric; goal and game widgets use their own data
 * paths and have no metrics by design.
 */
export function getVisibleDashboardWidgets(dashboard: DashboardData): DashboardWidget[] {
  return dashboard.widgets.filter((widget) => isPresetWidget(widget) || isNotEmpty(widget.metrics))
}

/**
 * Whether the widget spans a full feed row. Line and goal widgets always do,
 * as does a number widget with more than 3 metrics — multiple KPIs need the
 * horizontal space. Table, leaderboard and game widgets share a row.
 */
export function isFullRowFeedWidget(widget: DashboardWidget): boolean {
  switch (widget.type) {
    case 'goal':
    case 'line':
      return true
    case 'number':
      return widget.metrics.length >= NUMBER_WIDGET_FULL_ROW_METRIC_THRESHOLD
    case 'game':
    case 'leaderboard':
    case 'table':
      return false
  }
}

/**
 * The viewer's non-hustle dashboards that contribute at least one renderable
 * widget, in the viewer's dashboard order — each renders as its own feed
 * section under the dashboard's name. Dashboards with nothing to render are
 * dropped so they don't leave an empty header behind.
 *
 * The hustle dashboard is pinned separately at the top of the feed, so it is
 * excluded here to avoid duplication.
 */
export function getDashboardFeedGroups<T extends DashboardData>(dashboards: T[]): DashboardFeedGroup<T>[] {
  return [...dashboards]
    .filter((dashboard) => dashboard.home !== true)
    .sort(sortDashboardsByOrder)
    .map((dashboard) => ({ dashboard, widgets: getVisibleDashboardWidgets(dashboard) }))
    .filter((group) => isNotEmpty(group.widgets))
}

export function hasGameWidget(widgets: DashboardWidget[]): boolean {
  return widgets.some((widget) => widget.type === 'game')
}

export function withoutGameWidgets(widgets: DashboardWidget[]): DashboardWidget[] {
  return widgets.filter((widget) => widget.type !== 'game')
}

/**
 * Takes the competition widgets out of the feed, and any section they leave empty
 * so it doesn't keep a bare header. For a viewer with no competitions who can't
 * create one: the widget has nothing to show or offer them.
 */
export function hideGameWidgets<T extends DashboardData>(groups: DashboardFeedGroup<T>[]): DashboardFeedGroup<T>[] {
  return groups
    .map((group) => ({ ...group, widgets: withoutGameWidgets(group.widgets) }))
    .filter((group) => isNotEmpty(group.widgets))
}
